import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { NavGraph, NavGraphItem } from 'src/types/navGraph';
import {
    generateFilter,
    getFilteredNavGraph,
    getItemByPath,
    makeInitialFilter,
} from 'src/utils/hierarchyUtils';
import { FilterItem } from '../HierarchyFilters';
import { NAV_GRAPH_ROOT_NODE_ID, useNavGraph } from './NavGraphProvider';
import { usePreferences } from './PreferencesProvider';

export type TabContext = {
    path: string;
    isDynamic: boolean;
};

export interface BreadCrumbFilterContextType {
    breadCrumbFilters: FilterItem[];
    tabContext: Map<string, TabContext>;
}

export const BreadCrumbFilterContext =
    React.createContext<BreadCrumbFilterContextType>({
        breadCrumbFilters: [],
        tabContext: new Map(),
    });

export const useBreadCrumbFilters = () => useContext(BreadCrumbFilterContext);

interface BreadCrumbFilterProviderProps {
    children: React.ReactNode;
}

export const BreadCrumbFilterProvider = ({
    children,
}: BreadCrumbFilterProviderProps) => {
    const PATH_DELIMITER = '/';
    const { navGraph } = useNavGraph();
    const { refinementToggleOn, userPreferences } = usePreferences();
    const location = useLocation();

    const getCurrentTab = (): string | null => {
        if (location.hash.includes('tab=')) {
            try {
                return location.hash.split('tab=')[1].split('&')[0];
            } catch (e) {
                return null;
            }
        }
        if (location.search.includes('tab=')) {
            try {
                return location.search.split('tab=')[1].split('&')[0];
            } catch (e) {
                return null;
            }
        }
        return null;
    };

    const filteredGraph = React.useMemo(() => {
        if (!navGraph || !Object.keys(navGraph).length) {
            return {};
        }
        const graph = JSON.parse(JSON.stringify(navGraph)) as NavGraph;
        getFilteredNavGraph(graph, userPreferences, refinementToggleOn);
        return graph;
    }, [navGraph, userPreferences, refinementToggleOn]);

    const tabContext = React.useMemo(() => {
        const context: Map<string, TabContext> = new Map();
        if (!filteredGraph || !Object.keys(filteredGraph).length) {
            return context;
        }

        const stack: [string, NavGraphItem][] = Object.entries(filteredGraph);
        while (stack.length) {
            const [itemKey, currItem]: [string, NavGraphItem] = stack.pop()!;

            const path = currItem.pathSegments
                ? currItem.pathSegments
                      .filter(
                          (seg) => seg.id !== String(NAV_GRAPH_ROOT_NODE_ID)
                      )
                      .map((seg) => seg.id)
                      .join(PATH_DELIMITER)
                : itemKey;

            context.set(itemKey, { path, isDynamic: !!currItem.isDynamic });
            context.set(path, { path, isDynamic: !!currItem.documentId });

            if (currItem.children) {
                Object.entries(currItem.children).forEach(([key, c]) =>
                    stack.push([key, c])
                );
            }
        }
        return context;
    }, [filteredGraph]);

    const breadCrumbFilters = React.useMemo(() => {
        if (!filteredGraph || !Object.keys(filteredGraph).length) {
            return [];
        }

        const currentTab = getCurrentTab();
        if (!currentTab) {
            return makeInitialFilter(filteredGraph);
        }

        const pathForTab = tabContext.get(currentTab)?.path;
        if (!pathForTab) {
            return makeInitialFilter(filteredGraph);
        }

        const pathList: string[] = [];
        const ids = pathForTab.split(PATH_DELIMITER).filter(Boolean);
        ids.forEach((id, idx) => {
            pathList.push(ids.slice(0, idx + 1).join(PATH_DELIMITER));
        });

        const filters: FilterItem[] = [makeInitialFilter(filteredGraph)[0]];
        pathList.forEach((fullPath, idx) => {
            const item = getItemByPath(fullPath, filteredGraph);
            if (item) {
                if (idx >= 1) {
                    filters[idx].itemSelected = item.name;
                }
                filters.push(generateFilter(item));
            }
        });
        return filters;
    }, [location.hash, location.search, filteredGraph, tabContext]);

    const value = React.useMemo<BreadCrumbFilterContextType>(
        () => ({
            breadCrumbFilters,
            tabContext,
        }),
        [breadCrumbFilters, tabContext]
    );

    return (
        <BreadCrumbFilterContext.Provider value={value}>
            {children}
        </BreadCrumbFilterContext.Provider>
    );
};
